import { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  CloseRetainerDocument,
  CreateRetainerRequestDocument,
  DeclineRetainerDocument,
  MarkRetainerWorkActiveDocument,
  MarkRetainerWorkCompleteDocument,
  StartRetainerFundingDocument,
} from '~/graphql/mutations/retainerMutations';
import {
  MatterDetailDocument,
  MattersDocument,
} from '~/graphql/queries/matters';
import { RetainersForMatterDocument } from '~/graphql/queries/retainersForMatter';
import type {
  CreateRetainerRequestInput,
  RetainerFieldsFragment,
} from '~/gql/graphql';

function refetchRetainersAndMatterDetail(matterId: string) {
  return [
    { query: RetainersForMatterDocument, variables: { matterId } },
    { query: MatterDetailDocument, variables: { id: matterId } },
    { query: MattersDocument },
  ];
}

/**
 * Retainer GraphQL mutations with cache refresh for matter detail + retainers list.
 * Callers should `try` / `catch` and use `useToast` for user-visible errors (see modals e.g. RemoveExpertModal).
 */
export function useRetainerMutations() {
  const [
    createRetainerRequestMutation,
    { loading: isCreatingRetainerRequest },
  ] = useMutation(CreateRetainerRequestDocument);

  const [startRetainerFundingMutation, { loading: isStartingRetainerFunding }] =
    useMutation(StartRetainerFundingDocument);

  const [declineRetainerMutation, { loading: isDecliningRetainer }] =
    useMutation(DeclineRetainerDocument);

  const [closeRetainerMutation, { loading: isClosingRetainer }] = useMutation(
    CloseRetainerDocument,
  );

  const [markRetainerWorkCompleteMutation] = useMutation(
    MarkRetainerWorkCompleteDocument,
  );
  const [completingRetainerId, setCompletingRetainerId] = useState<
    string | null
  >(null);

  const [markRetainerWorkActiveMutation] = useMutation(
    MarkRetainerWorkActiveDocument,
  );
  const [activatingRetainerId, setActivatingRetainerId] = useState<
    string | null
  >(null);

  const createRetainerRequest = async (
    matterId: string,
    input: CreateRetainerRequestInput,
  ): Promise<RetainerFieldsFragment | null> => {
    const { data } = await createRetainerRequestMutation({
      variables: { input },
      refetchQueries: refetchRetainersAndMatterDetail(matterId),
    });
    return data?.createRetainerRequest ?? null;
  };

  /** No `refetchQueries`: callers typically `window.location.assign` to Stripe immediately after, so queued refetches would not run. */
  const startRetainerFunding = async (
    retainerId: string,
  ): Promise<string | null> => {
    const { data } = await startRetainerFundingMutation({
      variables: { retainerId },
    });
    return data?.startRetainerFunding ?? null;
  };

  const declineRetainer = async (
    matterId: string,
    retainerId: string,
    declinedMessage?: string,
  ): Promise<RetainerFieldsFragment | null> => {
    const { data } = await declineRetainerMutation({
      variables: { retainerId, declinedMessage },
      refetchQueries: refetchRetainersAndMatterDetail(matterId),
    });
    return data?.declineRetainer ?? null;
  };

  const closeRetainer = async (
    matterId: string,
    retainerId: string,
    closedMessage?: string,
  ): Promise<RetainerFieldsFragment | null> => {
    const { data } = await closeRetainerMutation({
      variables: { retainerId, closedMessage },
      refetchQueries: refetchRetainersAndMatterDetail(matterId),
    });
    return data?.closeRetainer ?? null;
  };

  const markRetainerWorkComplete = async (
    matterId: string,
    retainerId: string,
  ): Promise<RetainerFieldsFragment | null> => {
    setCompletingRetainerId(retainerId);
    try {
      const { data } = await markRetainerWorkCompleteMutation({
        variables: { retainerId },
        refetchQueries: refetchRetainersAndMatterDetail(matterId),
      });
      return data?.markRetainerWorkComplete ?? null;
    } finally {
      // Only clear if this call still owns the slot — otherwise a later overlapping
      // call has taken over and we'd wipe its spinner before it finishes.
      setCompletingRetainerId((current) =>
        current === retainerId ? null : current,
      );
    }
  };

  const markRetainerWorkActive = async (
    matterId: string,
    retainerId: string,
  ): Promise<RetainerFieldsFragment | null> => {
    setActivatingRetainerId(retainerId);
    try {
      const { data } = await markRetainerWorkActiveMutation({
        variables: { retainerId },
        refetchQueries: refetchRetainersAndMatterDetail(matterId),
      });
      return data?.markRetainerWorkActive ?? null;
    } finally {
      setActivatingRetainerId((current) =>
        current === retainerId ? null : current,
      );
    }
  };

  return {
    createRetainerRequest,
    startRetainerFunding,
    declineRetainer,
    closeRetainer,
    markRetainerWorkComplete,
    markRetainerWorkActive,
    isCreatingRetainerRequest,
    isStartingRetainerFunding,
    isDecliningRetainer,
    isClosingRetainer,
    /** Retainer ID currently being marked complete; null when no request is in flight. */
    completingRetainerId,
    /** Retainer ID currently being marked active (expert undo-complete); null when no request is in flight. */
    activatingRetainerId,
  };
}
